// The payment step of a continuous membership ("Jatkuva laskutus"): the first period is paid now with
// a stored card and each following period is charged from the same card on its renewal day, until
// the customer cancels. So instead of the usual payment methods this asks for exactly three things -
// which stored card, consent to the recurring charge, and confirmation - see utils/subscriptions.js.
import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Input, Label, Row, Col, Alert, Spinner } from 'reactstrap'
import Checkbox from '@material-ui/core/Checkbox'
import { useTranslation } from 'react-i18next'
import { cardList, defaultCardId } from '../utils/storedcards'

const money = (cents) => (cents / 100).toFixed(2).replace('.', ',') + ' €'

function SubscriptionCheckout({ cards, price, period, renewalDate, onAddCard, onConfirm, disabled, addingCard, addCardFailed }) {
  const { t } = useTranslation()
  const list = cardList(cards)
  const [cardid, setCardid] = useState(defaultCardId(list))
  const [consent, setConsent] = useState(false)

  // Cards arrive from Firestore after the first render: preselect once they do.
  const firstValid = defaultCardId(list)
  useEffect(() => {
    if (!cardid && firstValid) setCardid(firstValid)
  }, [cardid, firstValid])

  if (list.length === 0) {
    return (
      <div style={{ marginTop: 10 }}>
        <p>{t('subscription_needs_card')}</p>
        <Button color='primary' disabled={addingCard} onClick={onAddCard}>
          {addingCard ? <Spinner size='sm' /> : t('add_card')}
        </Button>
        <div style={{ fontSize: 'small', marginTop: 5 }}>{t('card_storage_note')}</div>
        {addCardFailed ? <div style={{ color: '#b00020', marginTop: 5 }}>{t('card_add_failed_init')}</div> : null}
      </div>
    )
  }

  const chosen = list.find((c) => c.id === cardid)
  const canConfirm = !disabled && consent && chosen && !chosen.expired

  return (
    <div style={{ marginTop: 10 }}>
      <Row className='loose'>
        <Col>{t('subscription_first_payment')}</Col>
        <Col className='text-right'>{money(price)}</Col>
      </Row>
      <Row className='loose'>
        <Col>{t('subscription_then')}</Col>
        <Col className='text-right'>
          {money(price)}
          {period ? ' / ' + period : ''}
        </Col>
      </Row>
      {renewalDate ? (
        <Row className='loose'>
          <Col>{t('subscription_next_charge')}</Col>
          <Col className='text-right'>{renewalDate}</Col>
        </Row>
      ) : null}

      <div style={{ marginTop: 15, marginBottom: 5 }}>
        <strong>{t('subscription_choose_card')}</strong>
      </div>
      {list.map((c) => (
        <FormGroup check key={c.id}>
          <Label check style={c.expired ? { color: '#999' } : null}>
            <Input
              type='radio'
              name='subscriptioncard'
              checked={cardid === c.id}
              disabled={c.expired}
              onChange={() => setCardid(c.id)}
            />{' '}
            <img src={process.env.PUBLIC_URL + c.icon} alt={c.type} style={{ height: 16, verticalAlign: 'middle', marginRight: 6 }} />
            {c.brandShown ? '' : c.type + ' '}{c.label} {c.expires}
            {c.expired ? ' — ' + t('card_expired_label') : ''}
          </Label>
        </FormGroup>
      ))}
      <Button size='sm' color='link' style={{ paddingLeft: 0 }} disabled={addingCard} onClick={onAddCard}>
        {addingCard ? <Spinner size='sm' /> : t('add_card')}
      </Button>
      {addCardFailed ? <div style={{ color: '#b00020' }}>{t('card_add_failed_init')}</div> : null}

      <Alert color='light' style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start' }}>
        <Checkbox
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          color='primary'
          style={{ paddingTop: 0 }}
        />
        <span>{t('subscription_consent')}</span>
      </Alert>

      <Button color='primary' block disabled={!canConfirm} onClick={() => onConfirm({ cardid })}>
        {t('subscription_start')}
      </Button>
    </div>
  )
}

export default SubscriptionCheckout
